import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, RefreshCw, ShieldAlert, Star } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  evaluateVisibility,
  publicAttribution,
  REASON_LABEL,
  todayISO,
  type TestimonialVisibilityInput,
} from "@/lib/testimonial-visibility";

type Row = TestimonialVisibilityInput & {
  id: string;
  full_name: string;
  role_title: string | null;
  organization: string | null;
  quote: string;
  context: string | null;
  rating: number | null;
};

const Stars = ({ rating }: { rating: number | null }) =>
  rating ? (
    <span className="flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {Array.from({ length: rating }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" aria-hidden="true" />
      ))}
    </span>
  ) : null;

const PublicCard = ({ row }: { row: Row }) => {
  const { name, detail } = publicAttribution(row);
  return (
    <Card className="border-gold/25">
      <CardContent className="space-y-3 p-5">
        <Stars rating={row.rating} />
        <blockquote className="text-sm leading-relaxed">“{row.quote}”</blockquote>
        <footer className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{name}</span>
          {detail ? ` — ${detail}` : ""}
        </footer>
        {row.context && <p className="text-xs text-muted-foreground">{row.context}</p>}
      </CardContent>
    </Card>
  );
};

const HiddenRow = ({ row, asOf }: { row: Row; asOf: string }) => {
  const { reasons } = evaluateVisibility(row, asOf);
  return (
    <Card className="border-dashed">
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium">{row.full_name}</span>
          <Badge variant="outline" className="text-[11px]">
            {row.status}
          </Badge>
        </div>
        <p className="line-clamp-2 text-xs text-muted-foreground">“{row.quote}”</p>
        <ul className="flex flex-wrap gap-1.5">
          {reasons.map((r) => (
            <li
              key={r}
              className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive"
            >
              {REASON_LABEL[r]}
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-muted-foreground">
          Window: {row.release_effective_date ?? "—"} → {row.release_expires_at ?? "no expiry"}
        </p>
      </CardContent>
    </Card>
  );
};

const AdminTestimonialsPreview = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [asOf, setAsOf] = useState(todayISO());

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .rpc("has_role", { _user_id: user.id, _role: "admin" })
      .then(({ data, error }) => setIsAdmin(!error && data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("testimonial_submissions")
      .select(
        "id, full_name, role_title, organization, quote, context, rating, status, published_at, consent_publish_quote, consent_publish_name, consent_publish_org, signature_name, signed_at, release_effective_date, release_expires_at",
      )
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data as Row[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const { shown, hidden } = useMemo(() => {
    const s: Row[] = [];
    const h: Row[] = [];
    for (const r of rows) {
      (evaluateVisibility(r, asOf).visible ? s : h).push(r);
    }
    return { shown: s, hidden: h };
  }, [rows, asOf]);

  if (authLoading || isAdmin === null) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <Button asChild>
          <Link to="/auth">Sign in</Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <Helmet>
        <title>Testimonial Preview | Admin</title>
        <meta
          name="description"
          content="Preview exactly which testimonials visitors see based on signed releases and active release windows."
        />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-6 space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Visitor preview</h1>
            <p className="text-sm text-muted-foreground">
              Applies the same rule as the public site: published, signed release, quote consent, and an
              active release window.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/testimonials">Moderation</Link>
            </Button>
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 rounded-md border bg-muted/40 p-3">
          <div className="space-y-1">
            <Label htmlFor="as-of" className="text-xs">
              Preview as of date
            </Label>
            <Input
              id="as-of"
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value || todayISO())}
              className="h-9 w-44"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={() => setAsOf(todayISO())}>
            Reset to today
          </Button>
          <p className="text-xs text-muted-foreground">
            {shown.length} visible · {hidden.length} hidden on {asOf}
          </p>
        </div>
      </header>

      <section className="space-y-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Eye className="h-4 w-4" aria-hidden="true" /> Visitors will see ({shown.length})
        </h2>
        {shown.length === 0 && (
          <p className="text-sm text-muted-foreground">No testimonials are publicly visible on this date.</p>
        )}
        {shown.map((row) => (
          <PublicCard key={row.id} row={row} />
        ))}
      </section>

      <section className="mt-10 space-y-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <EyeOff className="h-4 w-4" aria-hidden="true" /> Hidden ({hidden.length})
        </h2>
        {hidden.map((row) => (
          <HiddenRow key={row.id} row={row} asOf={asOf} />
        ))}
      </section>
    </main>
  );
};

export default AdminTestimonialsPreview;
